import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'

export type KeyboardFormLibraryDragData = {
  dropType: string
  dropValue: any
  previewLabel?: string
  sourceList?: 'blank' | 'premade'
  sourceIndex?: number
}

export type KeyboardFormLibraryDragState = {
  active: boolean
  dragData: KeyboardFormLibraryDragData | null
  sourceElement: HTMLElement | null
  destinationFocusRequestId: number
  inDestination: boolean
}

@Injectable({ providedIn: 'root' })
export class KeyboardFormLibraryDragService {
  private readonly _state$ = new BehaviorSubject<KeyboardFormLibraryDragState>({
    active: false,
    dragData: null,
    sourceElement: null,
    destinationFocusRequestId: 0,
    inDestination: false,
  })

  readonly state$ = this._state$.asObservable()

  get snapshot(): KeyboardFormLibraryDragState {
    return this._state$.getValue()
  }

  start(dragData: KeyboardFormLibraryDragData, sourceElement: HTMLElement) {
    const prev = this._state$.getValue()
    this._state$.next({
      active: true,
      dragData,
      sourceElement,
      destinationFocusRequestId: prev.destinationFocusRequestId,
      inDestination: false,
    })
  }

  requestDestinationFocus() {
    const prev = this._state$.getValue()
    if (!prev.active) {
      return
    }

    this._state$.next({
      ...prev,
      destinationFocusRequestId: prev.destinationFocusRequestId + 1,
      inDestination: true,
    })
  }

  cancel() {
    const prev = this._state$.getValue()
    this._state$.next({
      active: false,
      dragData: null,
      sourceElement: null,
      destinationFocusRequestId: prev.destinationFocusRequestId,
      inDestination: false,
    })

    this.restoreSourceFocus(prev.sourceElement, prev.dragData)
  }

  completeDrop() {
    const prev = this._state$.getValue()
    this._state$.next({
      active: false,
      dragData: null,
      sourceElement: null,
      destinationFocusRequestId: prev.destinationFocusRequestId,
      inDestination: false,
    })

    this.restoreSourceFocus(prev.sourceElement, prev.dragData)
  }

  private restoreSourceFocus(sourceElement: HTMLElement | null, sourceData: KeyboardFormLibraryDragData | null) {
    const selector = this.getSourceSelector(sourceData)

    setTimeout(() => {
      // First try the original element if it's still connected.
      if (sourceElement && document.contains(sourceElement)) {
        sourceElement.focus()
        return
      }

      // Fallback to a stable selector when DOM nodes are recreated after dialog close.
      if (selector) {
        const fallbackEl = document.querySelector(selector) as HTMLElement
        fallbackEl?.focus()
      }
    }, 100)
  }

  private getSourceSelector(sourceData: KeyboardFormLibraryDragData | null): string {
    if (!sourceData || sourceData.sourceIndex === undefined || sourceData.sourceIndex === null) {
      return ''
    }

    const listClass = sourceData.sourceList === 'blank' ? '.blank-form-field' : '.premade-field'
    return `${listClass}[data-kbd-index="${sourceData.sourceIndex}"]`
  }
}
